
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Header from "../Components/Header";

const API = "https://api.welbeing.in";

const parseResponse = async (response) => {
    let data;

    try {
        data = await response.json();
    } catch {
        throw new Error("Invalid response received from server.");
    }

    if (!response.ok) {
        throw new Error(
            data?.message || `Request failed (${response.status}).`
        );
    }

    return data;
};

const BannerSection = () => {
    const [bannerSections, setBannerSections] = useState([]);
    const [subcategory, setSubcategory] = useState([]);
    const [subId, setSubId] = useState("");
    const [heading, setHeading] = useState("");
    const [description, setDescription] = useState("");
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);
    const previewRef = useRef(null);
    const addingRef = useRef(false);
    const deletingRef = useRef(null);
    const mountedRef = useRef(false);

    const fetchSubCategories = async () => {
        const response = await axios.get(
            `${API}/admin/categories/subcategories/`
        );

        if (response.data?.status !== "success") {
            throw new Error(
                response.data?.message ||
                "Failed to fetch subcategories."
            );
        }

        setSubcategory(response?.data?.data || []);
    };

    const fetchBannerSections = async () => {
        const response = await fetch(
            `${API}/home/get_sub_banners/`
        );

        const data = await parseResponse(response);

        if (data.status !== "success" || !Array.isArray(data.data)) {
            throw new Error(
                data.message || "Failed to fetch banner sections."
            );
        }

        setBannerSections(data.data);
    };

    useEffect(() => {
        mountedRef.current = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const results = await Promise.allSettled([
                    fetchSubCategories(),
                    fetchBannerSections(),
                ]);

                if (!mountedRef.current) return;

                const errors = results
                    .filter((result) => result.status === "rejected")
                    .map((result) => result.reason?.message);

                if (errors.length) {
                    setError(errors.join(" "));
                }
            } catch {
                if (mountedRef.current) {
                    setError("Failed to load banner data.");
                }
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            mountedRef.current = false;

            if (previewRef.current) {
                URL.revokeObjectURL(previewRef.current);
                previewRef.current = null;
            }
        };
    }, []);

    const clearImage = () => {
        if (previewRef.current) {
            URL.revokeObjectURL(previewRef.current);
            previewRef.current = null;
        }

        setPreview(null);
        setImage(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.");
            event.target.value = "";
            return;
        }

        setError(null);

        if (previewRef.current) {
            URL.revokeObjectURL(previewRef.current);
        }

        const objectUrl = URL.createObjectURL(file);

        previewRef.current = objectUrl;
        setImage(file);
        setPreview(objectUrl);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError(null);
        setSuccessMessage("");

        if (!subId) {
            setError("Please select a subcategory.");
            return;
        }

        if (!heading.trim()) {
            setError("Heading is required.");
            return;
        }

        if (!description.trim()) {
            setError("Description is required.");
            return;
        }

        if (!image) {
            setError("Please select a banner image.");
            return;
        }

        if (addingRef.current) return;

        addingRef.current = true;
        setAdding(true);

        try {
            const formData = new FormData();

            formData.append("sub_id", subId);
            formData.append("heading", heading.trim());
            formData.append("description", description.trim());
            formData.append("image", image);

            const response = await fetch(
                `${API}/home/add_sub_banner/`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            const data = await parseResponse(response);

            if (data.status !== "success") {
                throw new Error(
                    data.message || "Failed to add banner section."
                );
            }

            // Refresh the list before clearing the form.
            await fetchBannerSections();

            if (!mountedRef.current) return;

            setSuccessMessage("Banner section added successfully!");
            setSubId("");
            setHeading("");
            setDescription("");
            clearImage();
        } catch (err) {
            if (mountedRef.current) {
                setError(
                    err.message || "Failed to add banner section."
                );
            }
        } finally {
            addingRef.current = false;

            if (mountedRef.current) {
                setAdding(false);
            }
        }
    };

    const handleDelete = async (id) => {
        if (deletingRef.current !== null) return;

        deletingRef.current = id;
        setDeletingId(id);
        setError(null);
        setSuccessMessage("");

        try {
            const response = await fetch(
                `${API}/home/delete-banner/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ id }),
                }
            );

            const data = await parseResponse(response);

            if (data.status !== "success") {
                throw new Error(
                    data.message || "Failed to delete banner section."
                );
            }

            if (!mountedRef.current) return;

            setBannerSections((previous) =>
                previous.filter((banner) => banner.id !== id)
            );

            setSuccessMessage(
                "Banner section removed successfully!"
            );
        } catch (err) {
            if (mountedRef.current) {
                setError(
                    err.message || "Failed to delete banner section."
                );
            }
        } finally {
            deletingRef.current = null;

            if (mountedRef.current) {
                setDeletingId(null);
            }
        }
    };

    return (
        <>
            <Header />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                {successMessage && (
                                    <div
                                        className="alert alert-success alert-dismissible fade show"
                                        role="status"
                                    >
                                        {successMessage}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={() =>
                                                setSuccessMessage("")
                                            }
                                        />
                                    </div>
                                )}

                                {error && (
                                    <div
                                        className="alert alert-danger alert-dismissible fade show"
                                        role="alert"
                                    >
                                        {error}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={() => setError(null)}
                                        />
                                    </div>
                                )}

                                <div className="mb-3">
                                    <h5
                                        className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                                        style={{
                                            fontSize: "30px",
                                            background:
                                                "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                                            borderLeft: "5px solid #0d6efd",
                                            color: "#0d6efd",
                                            letterSpacing: "0.3px",
                                        }}
                                    >
                                        Add Trending Products Section
                                    </h5>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="form-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="subcategory"
                                                        className="form-label"
                                                    >
                                                        Sub Category
                                                    </label>

                                                    <select
                                                        id="subcategory"
                                                        className="form-control"
                                                        name="sub_category"
                                                        value={subId}
                                                        onChange={(event) =>
                                                            setSubId(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={adding}
                                                    >
                                                        <option value="">
                                                            -- Select Sub
                                                            Category --
                                                        </option>

                                                        {subcategory?.map(
                                                            (sub) => (
                                                                <option
                                                                    key={
                                                                        sub.sub_id
                                                                    }
                                                                    value={
                                                                        sub.sub_id
                                                                    }
                                                                >
                                                                    {
                                                                        sub.sub_cate
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="heading"
                                                        className="form-label"
                                                    >
                                                        Heading
                                                    </label>

                                                    <input
                                                        id="heading"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter heading"
                                                        value={heading}
                                                        onChange={(event) =>
                                                            setHeading(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={adding}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="description"
                                                        className="form-label"
                                                    >
                                                        Description
                                                    </label>

                                                    <textarea
                                                        id="description"
                                                        className="form-control"
                                                        placeholder="Enter description"
                                                        rows="1"
                                                        value={description}
                                                        onChange={(event) =>
                                                            setDescription(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={adding}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="banner-image"
                                                        className="form-label"
                                                    >
                                                        Banner Image
                                                    </label>

                                                    <input
                                                        id="banner-image"
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/*"
                                                        ref={fileInputRef}
                                                        onChange={
                                                            handleImageChange
                                                        }
                                                        disabled={adding}
                                                    />

                                                    {preview && (
                                                        <div className="mt-2 position-relative d-inline-block">
                                                            <img
                                                                src={preview}
                                                                alt="Banner preview"
                                                                style={{
                                                                    width: "120px",
                                                                    height: "70px",
                                                                    objectFit:
                                                                        "cover",
                                                                    borderRadius:
                                                                        "8px",
                                                                    border: "1px solid #dee2e6",
                                                                }}
                                                            />

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                aria-label="Remove selected image"
                                                                disabled={
                                                                    adding
                                                                }
                                                                style={{
                                                                    position:
                                                                        "absolute",
                                                                    top: "-8px",
                                                                    right: "-8px",
                                                                    borderRadius:
                                                                        "50%",
                                                                    width: "22px",
                                                                    height: "22px",
                                                                    padding: 0,
                                                                    fontSize:
                                                                        "12px",
                                                                    lineHeight:
                                                                        "1",
                                                                }}
                                                                onClick={
                                                                    clearImage
                                                                }
                                                            >
                                                                <i className="ti ti-x" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-light-primary text-primary font-medium mb-3"
                                                    disabled={adding}
                                                >
                                                    {adding ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                            />
                                                            Adding…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ti ti-plus me-1" />
                                                            Add
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>

                                <hr />

                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                                    <h5 className="mb-0">
                                        Trending Products List
                                    </h5>

                                    <span className="badge bg-dark fs-6">
                                        {bannerSections.length}{" "}
                                        {bannerSections.length === 1
                                            ? "Section"
                                            : "Sections"}
                                    </span>
                                </div>

                                <div
                                    className="table-responsive"
                                    style={{ overflowX: "auto" }}
                                >
                                    <table className="table border table-striped table-bordered align-middle">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Image</th>
                                                <th>Heading</th>
                                                <th>Description</th>
                                                <th className="text-center">
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        className="text-center py-4"
                                                    >
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                        Loading banner
                                                        sections…
                                                    </td>
                                                </tr>
                                            ) : bannerSections.length ===
                                              0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        className="text-center fw-bold"
                                                    >
                                                        No banner sections
                                                        added yet.
                                                    </td>
                                                </tr>
                                            ) : (
                                                bannerSections.map(
                                                    (section, index) => (
                                                        <tr key={section.id}>
                                                            <td>
                                                                {index + 1}
                                                            </td>

                                                            <td>
                                                                <img
                                                                    src={`${API}/uploads/sub_banners/${section.image}`}
                                                                    alt={
                                                                        section.heading ||
                                                                        "Banner"
                                                                    }
                                                                    style={{
                                                                        width: "90px",
                                                                        height: "55px",
                                                                        objectFit:
                                                                            "cover",
                                                                        borderRadius:
                                                                            "6px",
                                                                        border: "1px solid #dee2e6",
                                                                    }}
                                                                    onError={(
                                                                        event
                                                                    ) => {
                                                                        event.currentTarget.onerror =
                                                                            null;
                                                                        event.currentTarget.src =
                                                                            "/customer_data/no_image.webp";
                                                                    }}
                                                                />
                                                            </td>

                                                            <td>
                                                                <h6 className="mb-0">
                                                                    {
                                                                        section.heading
                                                                    }
                                                                </h6>
                                                            </td>

                                                            <td
                                                                style={{
                                                                    maxWidth:
                                                                        "200px",
                                                                }}
                                                            >
                                                                <p
                                                                    className="mb-0 text-muted"
                                                                    style={{
                                                                        overflow:
                                                                            "hidden",
                                                                        textOverflow:
                                                                            "ellipsis",
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                    }}
                                                                    title={
                                                                        section.description
                                                                    }
                                                                >
                                                                    {
                                                                        section.description
                                                                    }
                                                                </p>
                                                            </td>

                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    aria-label={`Delete ${
                                                                        section.heading ||
                                                                        "banner"
                                                                    }`}
                                                                    onClick={() =>
                                                                        handleDelete(
                                                                            section.id
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        deletingId !==
                                                                            null ||
                                                                        adding
                                                                    }
                                                                >
                                                                    {deletingId ===
                                                                    section.id ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm me-1" />
                                                                            Removing…
                                                                        </>
                                                                    ) : (
                                                                        <i className="ti ti-trash" />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BannerSection;
